import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import Condition from "../condition/Condition";
import { useToast } from "../../context/toast/ToastContext";
import ProductWithQuantity, {
  useGenerateProductWithQuantity,
  ProductWithQuantityResult,
} from "./productWithQuantity/ProductWithQuantity";
import TableFC, { TableFCBodyProps, TableHeadProps } from "../table/Table";
import { ToMoneyFormat } from "../../utils/Helper";
import { useGetProducts } from "../../features/produk/api/product/resolver";
import Input from "../input/Input-v2";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useGetInvoiceConstant } from "../../features/invoice/api/invoice/constant/resolver";

export interface ProductWithPriceInvoiceData {
  id: string;
  quantity: number;
  name: string;
  unitType: string;
  price: number;
  checked?: boolean;
}

interface ProductWithPriceInvoice {
  invoiceType?: string;
  dataProduct?: ProductWithPriceInvoiceData[];
  showCheckBox?: boolean;
  onChange(item: any[]): void;
}

export default function ProductWithPriceInvoice({
  invoiceType,
  dataProduct,
  onChange,
  showCheckBox,
}: ProductWithPriceInvoice) {
  const { show } = useToast();
  const [keywords, setKeywords] = useState("");
  const { data: dataRawProduct } = useGetProducts({
    keyword: keywords,
  });
  const dataPro = dataRawProduct?.data.data.data;
  const { data: dataProductOptions } = useGenerateProductWithQuantity({
    dataSource: dataPro,
    keyForText: "product_type_name",
    keyForValue: "id",
    keyForStock: "stock",
  });
  const { data: dataRawInvoiceConstant } = useGetInvoiceConstant();
  const dataInvoiceConstant = dataRawInvoiceConstant?.data.data.type;
  const [cart, setCart] = useState<ProductWithPriceInvoiceData[]>([]);
  const header: TableHeadProps[] = [
    { title: "No" },
    { title: "Nama Produk" },
    { title: "Satuan" },
    { title: "Harga" },
    { title: "Jumlah" },
    { title: "Total" },
    {
      title: "Action",
      show: dataProduct === undefined || dataProduct?.length <= 0,
    },
  ];
  const [body, setBody] = useState<TableFCBodyProps[]>([]);

  useEffect(() => {
    //Table Body
    setBody([
      { key: "name" },
      { key: "unitType" },
      {
        key: "",
        component: (item: ProductWithPriceInvoiceData) => (
          <Box>
            {dataProduct === undefined || dataProduct?.length <= 0 ? (
              <Box width={130}>
                <Input.Text
                  label=""
                  value={item.price}
                  onChange={(e) => handlePriceChange(e, item.id)}
                />
              </Box>
            ) : (
              <Box>{ToMoneyFormat(item.price)}</Box>
            )}
          </Box>
        ),
      },
      {
        key: "",
        component: (item: ProductWithPriceInvoiceData) => (
          <Box width={130}>
            <Input.Text
              label=""
              type="number"
              value={item.quantity}
              onChange={(e) => handleQuantityChange(e, item.id)}
            />
          </Box>
        ),
      },
      {
        key: "",
        component: (item: ProductWithPriceInvoiceData) => (
          <Box width={130}>{ToMoneyFormat(item.price * item.quantity)}</Box>
        ),
      },
      {
        key: "",
        show: dataProduct === undefined || dataProduct?.length <= 0,
        component: (item: ProductWithPriceInvoiceData) => (
          <Box width={50} display={"flex"} justifyContent={"center"}>
            <HighlightOffIcon onClick={() => handleDeleteCartItem(item.id)} />
          </Box>
        ),
      },
    ]);

    if (dataProduct) {
      setCart(
        dataProduct.map((item) => {
          return {
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            unitType: item.unitType,
          };
        })
      );
    }
  }, [dataProduct]);

  useEffect(() => {
    const productList: any[] = [];
    cart.forEach((item) => {
      productList.push({
        product_id: item.id,
        quantity: item.quantity,
        price: item.price,
        checked: item.checked,
      });
    });
    onChange(productList);
  }, [cart]);

  const handleAddCart = (item: ProductWithQuantityResult) => {
    if (cart.find((itemCart) => itemCart.id === item.value)) {
      show("Produk ini sudah ada", "warning");
      return;
    }
    const isGrosir = dataInvoiceConstant?.Grosir === invoiceType;
    setCart([
      ...cart,
      {
        id: item.value,
        // name: item.text,
        name: `${item.ext.product_category.category_name} - ${item.ext.product_brand.brand_name} - ${item.text}`,
        price: isGrosir ? item.ext.wholesale_price : item.ext.price,
        quantity: item.quantity,
        unitType: item.ext.unit_type,
      },
    ]);
  };

  const handleDeleteCartItem = (id: string) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  const handleQuantityChange = (value: string, id: string) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          item.quantity = parseInt(value);
        }
        return item;
      })
    );
  };

  const handlePriceChange = (value: string, id: string) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          item.price = parseInt(value);
        }
        return item;
      })
    );
  };

  const handleCheckedChange = (target: any[]) => {
    setCart((prev) => {
      return prev.map((item, indexCart) => {
        item.checked = false;
        if (target.find((itemTarget) => itemTarget.index === indexCart)) {
          item.checked = true;
        }
        return item;
      });
    });
  };

  return (
    <React.Fragment>
      <Condition.IF condition={!dataProduct || dataProduct?.length <= 0}>
        <ProductWithQuantity
          options={dataProductOptions}
          onChange={(e) => handleAddCart(e)}
          onKeyUp={(e) => setKeywords(e)}
        />
      </Condition.IF>
      <Box mt={3}>
        <Condition.IF condition={cart.length > 0}>
          <TableFC
            showCheckBox={showCheckBox}
            showIndex
            data={cart}
            body={body}
            head={header}
            lastRowRender={[
              <></>,
              <></>,
              <></>,
              <></>,
              <Typography>Grand Total:</Typography>,
              <Typography fontWeight={"bold"}>
                {ToMoneyFormat(
                  cart.reduce((acc, item) => {
                    return acc + item.price * item.quantity;
                  }, 0)
                )}
              </Typography>,
            ]}
            onChecked={(e) => handleCheckedChange(e)}
          />
        </Condition.IF>
      </Box>
    </React.Fragment>
  );
}
